import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, MessageCircle, Check } from "lucide-react";
import { waLink } from "../../lib/arvi";

const STEPS = [
  {
    key: "type",
    label: "The Piece",
    question: "What are you commissioning?",
    options: [
      { id: "saree", label: "Saree", hint: "Silk · daily luxury" },
      { id: "suit", label: "Suit Set", hint: "Everyday heirloom" },
      { id: "bridal", label: "Bridal", hint: "The day of days" },
    ],
  },
  {
    key: "fabric",
    label: "Fabric",
    question: "Which canvas calls to you?",
    options: [
      { id: "pure-silk", label: "Pure Silk", hint: "The classic ARVI base" },
      { id: "tussar", label: "Tussar Silk", hint: "Earthy, textured" },
      { id: "organza", label: "Organza", hint: "Sheer, ethereal" },
      { id: "cotton", label: "Cotton", hint: "Breathable, daily" },
    ],
  },
  {
    key: "palette",
    label: "Palette",
    question: "What colours should the artist reach for?",
    options: [
      { id: "maroon-gold", label: "Maroon & Gold", hint: "The ARVI signature" },
      { id: "ivory-pastels", label: "Ivory & Pastels", hint: "Soft, romantic" },
      { id: "jewel-tones", label: "Jewel Tones", hint: "Deep, regal" },
      { id: "earthy", label: "Earthy & Natural", hint: "Calm, grounded" },
      { id: "custom-palette", label: "I'll Share Inspiration", hint: "Bring your own" },
    ],
  },
  {
    key: "motifs",
    label: "Motifs",
    question: "Which motifs should her brush dwell on?",
    options: [
      { id: "florals", label: "Florals", hint: "Lotus, rose, jasmine" },
      { id: "peacocks", label: "Peacocks", hint: "Plumes, paisley" },
      { id: "geometric", label: "Geometric", hint: "Borders, lattice" },
      { id: "narrative", label: "Narrative", hint: "Tell me your story" },
      { id: "minimal", label: "Minimal", hint: "Quiet, considered" },
    ],
  },
  {
    key: "budget",
    label: "Investment",
    question: "What range feels right?",
    options: [
      { id: "18-30", label: "₹18,000 – ₹30,000", hint: "Simple Elegance" },
      { id: "25-50", label: "₹25,000 – ₹50,000", hint: "Custom Narrative" },
      { id: "50-80", label: "₹50,000 – ₹80,000", hint: "Detailed Artistry" },
      { id: "80+", label: "₹80,000+", hint: "Bridal Canvas" },
      { id: "guide-me", label: "Guide Me", hint: "Suggest based on design" },
    ],
  },
  {
    key: "timeline",
    label: "Timeline",
    question: "When do you need it in your hands?",
    options: [
      { id: "4-6", label: "4 – 6 weeks", hint: "Faster turnaround" },
      { id: "2-3m", label: "2 – 3 months", hint: "Standard couture pace" },
      { id: "flex", label: "I'm flexible", hint: "Take the time it needs" },
    ],
  },
];

export default function Configurator() {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [answers, setAnswers] = useState({});

  const isLast = step === STEPS.length;
  const total = STEPS.length;
  const progress = useMemo(
    () => Math.round((Math.min(step, total) / total) * 100),
    [step, total]
  );

  const current = STEPS[step];

  const select = (key, opt) => {
    setAnswers((a) => ({ ...a, [key]: opt }));
    if (step < total) setTimeout(() => setStep((s) => s + 1), 180);
  };

  const message = useMemo(() => {
    const lines = [
      "Hello ARVI 🌹",
      "",
      "I designed my piece using the ARVI configurator:",
      "",
    ];
    if (name) lines.push(`• Name: ${name}`);
    STEPS.forEach((s) => {
      const v = answers[s.key];
      if (v) lines.push(`• ${s.label}: ${v.label}`);
    });
    lines.push("", "Could we discuss design directions and pricing? Thank you!");
    return lines.join("\n");
  }, [answers, name]);

  const reset = () => {
    setStep(0);
    setAnswers({});
    setName("");
  };

  return (
    <section
      id="design"
      data-testid="configurator-section"
      className="bg-ivory py-24 md:py-32"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="text-center mb-14 reveal">
          <p className="text-overline text-gold mb-5">Design Your Heirloom</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-maroon tracking-tight leading-[1.05]">
            Six gentle questions,
            <br />
            <span className="italic">one painting that's yours.</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-maroon/70 text-[15px] leading-relaxed">
            Answer a few questions and we'll send your preferences straight to
            the artist — your WhatsApp will arrive pre-filled, ready to begin.
          </p>
        </div>

        {/* Configurator card */}
        <div
          data-testid="configurator-card"
          className="relative border border-[hsl(var(--gold)/0.6)] bg-parchment p-8 md:p-12 reveal"
        >
          {/* Progress */}
          <div className="flex items-center justify-between mb-10">
            <p className="text-overline text-maroon/60">
              Step {Math.min(step + 1, total)} <span className="text-gold">/</span> {total}
            </p>
            <div className="flex-1 mx-6 h-px bg-maroon/15 relative overflow-hidden">
              <span
                className="absolute inset-y-0 left-0 bg-gold transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-overline text-maroon/60 hidden sm:block">
              {isLast ? "Ready" : current?.label}
            </p>
          </div>

          {!isLast && current && (
            <div data-testid={`configurator-step-${current.key}`}>
              <p className="font-display italic text-gold text-lg mb-3">
                No. 0{step + 1}
              </p>
              <h3 className="font-display text-2xl md:text-[34px] text-maroon leading-tight">
                {current.question}
              </h3>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {current.options.map((opt) => {
                  const selected = answers[current.key]?.id === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => select(current.key, opt)}
                      data-testid={`configurator-option-${current.key}-${opt.id}`}
                      className={`group text-left p-5 border transition-all duration-300 ${
                        selected
                          ? "border-gold bg-maroon text-ivory"
                          : "border-maroon/15 bg-ivory hover:border-gold hover:-translate-y-[2px]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`font-display text-lg leading-tight ${
                            selected ? "text-ivory" : "text-maroon"
                          }`}
                        >
                          {opt.label}
                        </span>
                        {selected && (
                          <Check className="h-4 w-4 text-gold shrink-0 mt-1" />
                        )}
                      </div>
                      <p
                        className={`mt-2 text-[12px] tracking-wide ${
                          selected ? "text-ivory/70" : "text-maroon/55"
                        }`}
                      >
                        {opt.hint}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid="configurator-back"
                  className="inline-flex items-center gap-2 text-overline text-maroon/70 hover:text-gold disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.min(total, s + 1))}
                  disabled={!answers[current.key]}
                  data-testid="configurator-next"
                  className="inline-flex items-center gap-2 px-5 py-3 text-overline bg-maroon text-ivory hover:bg-gold hover:text-maroon disabled:opacity-30 disabled:hover:bg-maroon disabled:hover:text-ivory transition-colors"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {isLast && (
            <div data-testid="configurator-summary">
              <p className="font-display italic text-gold text-lg mb-3">
                Final Brushstroke
              </p>
              <h3 className="font-display text-2xl md:text-[34px] text-maroon leading-tight">
                Your design brief, ready for the artist.
              </h3>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                {STEPS.map((s) => (
                  <div
                    key={s.key}
                    className="flex items-baseline justify-between gap-4 border-b border-maroon/10 pb-3"
                  >
                    <span className="text-overline text-maroon/60">{s.label}</span>
                    <span className="font-display text-maroon text-right">
                      {answers[s.key]?.label || "—"}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <label
                  htmlFor="cfg-name"
                  className="text-overline text-maroon/60 block mb-2"
                >
                  Your Name (optional)
                </label>
                <input
                  id="cfg-name"
                  data-testid="configurator-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="So the artist knows who to greet"
                  className="w-full bg-ivory border border-maroon/20 px-4 py-3 font-sans-body text-maroon placeholder:text-maroon/35 focus:outline-none focus:border-gold transition-colors"
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={reset}
                  data-testid="configurator-reset"
                  className="inline-flex items-center gap-2 text-overline text-maroon/70 hover:text-gold transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Start Over
                </button>
                <a
                  href={waLink(message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="configurator-whatsapp-cta"
                  className="btn-cta-base btn-wa"
                >
                  <MessageCircle className="h-4 w-4" />
                  Send Brief on WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
